import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Storage, Auth } from 'aws-amplify';
import clientDB from '../config/clientDatabase';
import taskDB from '../config/taskDatabaseAPI';
import PowerBIManager from './PowerBIManager';
import KeywordDataBrowser from './KeywordDataBrowser';
import './ClientManager.css';

const ClientManager = ({ user, company }) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPowerBIModal, setShowPowerBIModal] = useState(false);
  const [powerBIClientId, setPowerBIClientId] = useState(null);
  const [powerBIClientName, setPowerBIClientName] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteClientId, setInviteClientId] = useState(null);
  const [inviteClientName, setInviteClientName] = useState('');
  const [newInvite, setNewInvite] = useState({
    email: '',
    name: '',
    role: 'viewer'
  });
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserData, setEditUserData] = useState({
    name: '',
    email: '',
    role: ''
  });
  const [expandedEmails, setExpandedEmails] = useState({});
  const [clientFiles, setClientFiles] = useState({});
  const [loadingFiles, setLoadingFiles] = useState({});
  const [copiedLink, setCopiedLink] = useState(null);
  const [newClient, setNewClient] = useState({
    name: '',
    primaryColor: '#1e40af',
    secondaryColor: '#3b82f6',
    maxTeamMembers: 10,
    storageLimit: 10,
    adminEmail: '',
    adminName: ''
  });

  useEffect(() => {
    loadClientsData();
  }, []);

  const loadClientsData = async () => {
    setLoading(true);
    try {
      // Get all client companies
      const clientCompanies = clientDB.getClientCompanies();

      // Enrich with user data and activity
      const enrichedClients = clientCompanies.map(client => {
        const users = clientDB.getCompanyUsers(client.id);

        // Simulate last login times and file upload activity
        const usersWithActivity = users.map(user => ({
          ...user,
          lastLogin: new Date(Date.now() - Math.random() * 1000 * 60 * 60 * 24 * 30), // Random within 30 days
          filesUploaded: Math.floor(Math.random() * 50),
          lastUpload: new Date(Date.now() - Math.random() * 1000 * 60 * 60 * 24 * 7) // Random within 7 days
        }));

        // Calculate client-level stats
        const totalUsers = usersWithActivity.length;
        const activeUsers = usersWithActivity.filter(u =>
          u.lastLogin > new Date(Date.now() - 1000 * 60 * 60 * 24 * 7)
        ).length;
        const totalFiles = usersWithActivity.reduce((sum, u) => sum + u.filesUploaded, 0);
        const lastActivity = usersWithActivity.length > 0
          ? new Date(Math.max(...usersWithActivity.map(u => u.lastLogin.getTime())))
          : null;
        const storageUsed = Math.floor(Math.random() * 10) + 1; // GB

        return {
          ...client,
          users: usersWithActivity,
          stats: {
            totalUsers,
            activeUsers,
            totalFiles,
            storageUsed,
            lastActivity
          },
          status: client.status || 'active' // Use database status or default to active
        };
      });

      setClients(enrichedClients);
    } catch (error) {
      console.error('Error loading clients data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const formatFileSize = (gb) => {
    return gb < 1 ? `${Math.round(gb * 1024)} MB` : `${gb.toFixed(1)} GB`;
  };

  const filteredAndSortedClients = clients
    .filter(client => {
      const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || client.status === filterStatus;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'users':
          return b.stats.totalUsers - a.stats.totalUsers;
        case 'activity':
          if (!a.stats.lastActivity && !b.stats.lastActivity) return 0;
          if (!a.stats.lastActivity) return 1;
          if (!b.stats.lastActivity) return -1;
          return b.stats.lastActivity - a.stats.lastActivity;
        case 'files':
          return b.stats.totalFiles - a.stats.totalFiles;
        default:
          return 0;
      }
    });

  const handleAddClient = () => {
    setShowAddModal(true);
  };

  const handleClientClick = async (client) => {
    const isExpanding = selectedClient?.id !== client.id;
    setSelectedClient(isExpanding ? client : null);

    // Load files when expanding
    if (isExpanding && !clientFiles[client.id]) {
      // Use client.id for the folder path (e.g., "juni"), not client.name (e.g., "Juni")
      await loadClientFiles(client.id, client.id);
    }
  };

  const loadClientFiles = async (clientId, clientName) => {
    setLoadingFiles(prev => ({ ...prev, [clientId]: true }));
    try {
      console.log('Loading files for client:', clientName, 'ID:', clientId);

      // Try to list all files under clients/{clientName}/
      const result = await Storage.list(`clients/${clientName}/`, {
        level: 'public',
        pageSize: 1000  // Get more files
      });

      console.log('S3 Storage.list result:', result);
      console.log('Number of results:', result.results?.length || 0);

      if (!result.results || result.results.length === 0) {
        console.warn('No files found for path:', `clients/${clientName}/`);
        setClientFiles(prev => ({ ...prev, [clientId]: [] }));
        return;
      }

      const filesWithDetails = await Promise.all(
        result.results
          .filter(item => {
            const hasSize = item.size > 0;
            const isFile = !item.key.endsWith('/');
            console.log('File item:', item.key, 'size:', item.size, 'isFile:', isFile);
            return hasSize && isFile;
          })
          .map(async (item) => {
            try {
              const url = await Storage.get(item.key, { level: 'public', expires: 604800 });
              const fileDetails = {
                key: item.key,
                name: item.key.split('/').pop(),
                size: item.size,
                lastModified: item.lastModified,
                url: url
              };
              console.log('Processed file:', fileDetails);
              return fileDetails;
            } catch (error) {
              console.error(`Error getting URL for ${item.key}:`, error);
              return null;
            }
          })
      );

      const validFiles = filesWithDetails.filter(f => f !== null);
      console.log('Valid files to display:', validFiles.length, validFiles);
      setClientFiles(prev => ({ ...prev, [clientId]: validFiles }));
    } catch (error) {
      console.error('Error loading client files:', error);
      setClientFiles(prev => ({ ...prev, [clientId]: [] }));
    } finally {
      setLoadingFiles(prev => ({ ...prev, [clientId]: false }));
    }
  };

  const isHtmlFile = (fileName) => {
    return fileName.toLowerCase().endsWith('.html') || fileName.toLowerCase().endsWith('.htm');
  };

  const generatePublicLink = async (fileKey, fileName) => {
    try {
      const cloudFrontDomain = 'reports.1209digital.com';
      const fullPath = fileKey.startsWith('public/') ? fileKey : `public/${fileKey}`;
      const publicUrl = `https://${cloudFrontDomain}/${fullPath}`;

      // Copy to clipboard
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(publicUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = publicUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopiedLink(fileKey);
      setTimeout(() => setCopiedLink(null), 3000);

      return publicUrl;
    } catch (error) {
      console.error('Error generating public link:', error);
      alert(`Failed to generate public link. Please try again.\n\nError: ${error.message}`);
      return null;
    }
  };

  const downloadFile = async (fileKey, fileName) => {
    try {
      const url = await Storage.get(fileKey, { level: 'public', expires: 3600 });
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file. Please try again.');
    }
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleCreateClient = async () => {
    try {
      // Create the company
      const clientId = clientDB.createCompany({
        name: newClient.name,
        primaryColor: newClient.primaryColor,
        secondaryColor: newClient.secondaryColor,
        maxTeamMembers: newClient.maxTeamMembers,
        fileTypes: ['pdf', 'doc', 'xls', 'ppt'],
        storageLimit: newClient.storageLimit * 1024 * 1024 * 1024 // Convert GB to bytes
      });

      // Create invitation for admin user
      const inviteId = clientDB.createInvitation(
        user.attributes?.email || user.username, // inviter
        newClient.adminEmail, // invitee
        clientId,
        'admin'
      );

      // Generate invitation link
      const inviteLink = `${window.location.origin}/invite/${inviteId}`;

      // Reset form and close modal
      const clientName = newClient.name;
      const adminEmail = newClient.adminEmail;

      setNewClient({
        name: '',
        primaryColor: '#1e40af',
        secondaryColor: '#3b82f6',
        maxTeamMembers: 10,
        storageLimit: 10,
        adminEmail: '',
        adminName: ''
      });
      setShowAddModal(false);

      // Reload clients data
      loadClientsData();

      // Show invitation details
      alert(`Client "${clientName}" created successfully!\n\nInvitation sent to: ${adminEmail}\n\nInvitation Link:\n${inviteLink}\n\nShare this link with your client to complete their setup.`);

      // Copy invitation link to clipboard
      navigator.clipboard.writeText(inviteLink).then(() => {
        console.log('Invitation link copied to clipboard');
      }).catch(() => {
        console.log('Could not copy invitation link to clipboard');
      });

    } catch (error) {
      console.error('Error creating client:', error);
      alert('Error creating client. Please try again.');
    }
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setNewClient({
      name: '',
      primaryColor: '#1e40af',
      secondaryColor: '#3b82f6',
      maxTeamMembers: 10,
      storageLimit: 10,
      adminEmail: '',
      adminName: ''
    });
  };

  const handleManagePowerBI = (clientId, clientName) => {
    setPowerBIClientId(clientId);
    setPowerBIClientName(clientName);
    setShowPowerBIModal(true);
  };

  const handleClosePowerBI = () => {
    setShowPowerBIModal(false);
    setPowerBIClientId(null);
    setPowerBIClientName('');
  };

  const handleInviteUser = (clientId, clientName) => {
    setInviteClientId(clientId);
    setInviteClientName(clientName);
    setShowInviteModal(true);
  };

  const handleViewFiles = (clientId, clientName) => {
    // Navigate to upload page with pre-selected client and switch to manage tab
    navigate('/upload', {
      state: {
        selectedClient: clientId,
        activeTab: 'manage',
        clientName: clientName
      }
    });
  };

  const handleViewAsClient = (client) => {
    // Store the original admin context in localStorage for returning later
    localStorage.setItem('adminContext', JSON.stringify({
      user: user,
      company: company,
      returnUrl: '/clients'
    }));

    // Navigate to files page with client context
    navigate('/client-preview', {
      state: {
        clientCompany: client,
        previewMode: true
      }
    });
  };

  const handleCloseInvite = () => {
    setShowInviteModal(false);
    setInviteClientId(null);
    setInviteClientName('');
    setNewInvite({
      email: '',
      name: '',
      role: 'viewer'
    });
  };

  const handleSendInvitation = async () => {
    if (!newInvite.email || !newInvite.name) {
      alert('Please fill in both email and name');
      return;
    }

    try {
      // Create invitation
      const inviteId = clientDB.createInvitation(
        user.attributes?.email || user.username, // inviter
        newInvite.email, // invitee
        inviteClientId,
        newInvite.role
      );

      // Generate invitation link
      const inviteLink = `${window.location.origin}/invite/${inviteId}`;

      // Close modal and reset form
      handleCloseInvite();

      // Reload clients data to show updated stats
      loadClientsData();

      // Show invitation details
      alert(`Invitation sent successfully!\n\nUser: ${newInvite.name}\nEmail: ${newInvite.email}\nRole: ${newInvite.role}\n\nInvitation Link:\n${inviteLink}\n\nShare this link with the user to complete their setup.`);

      // Copy invitation link to clipboard
      navigator.clipboard.writeText(inviteLink).then(() => {
        console.log('Invitation link copied to clipboard');
      }).catch(() => {
        console.log('Could not copy invitation link to clipboard');
      });

    } catch (error) {
      console.error('Error creating invitation:', error);
      alert('Error creating invitation. Please try again.');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditUserData({
      name: user.name,
      email: user.email,
      role: user.role
    });
    setShowEditUserModal(true);
  };

  const handleCloseEditUser = () => {
    setShowEditUserModal(false);
    setEditingUser(null);
    setEditUserData({
      name: '',
      email: '',
      role: ''
    });
  };

  const handleUpdateUser = async () => {
    if (!editUserData.name || !editUserData.email || !editUserData.role) {
      alert('Please fill in all fields');
      return;
    }

    try {
      // Update user name if changed
      if (editUserData.name !== editingUser.name) {
        const userData = clientDB.getUser(editingUser.email);
        if (userData) {
          // Update user in database
          const updatedUser = { ...userData, name: editUserData.name };
          // Since we're using localStorage, we need to update the users object
          const allUsers = JSON.parse(localStorage.getItem('users') || '{}');
          allUsers[editingUser.email] = updatedUser;
          localStorage.setItem('users', JSON.stringify(allUsers));
        }
      }

      // Update user role if changed
      if (editUserData.role !== editingUser.role) {
        clientDB.updateUserRole(editingUser.email, editUserData.role);
      }

      // Handle email change (more complex - would need to update user key)
      if (editUserData.email !== editingUser.email) {
        alert('Email changes require the user to be re-invited. Please remove this user and invite them with the new email.');
        handleCloseEditUser();
        return;
      }

      // Close modal and reload data
      handleCloseEditUser();
      loadClientsData();

      alert(`User ${editUserData.name} updated successfully!`);

    } catch (error) {
      console.error('Error updating user:', error);
      alert('Error updating user. Please try again.');
    }
  };

  const toggleEmailBody = (emailId) => {
    setExpandedEmails(prev => ({
      ...prev,
      [emailId]: !prev[emailId]
    }));
  };

  if (loading) {
    return (
      <div className="client-manager loading">
        <div className="loading-spinner"></div>
        <p>Loading clients...</p>
      </div>
    );
  }

  return (
    <div className="client-manager">
      {/* Header */}
      <div className="clients-header">
        <div className="header-content">
          <h1>Client Management</h1>
          <p>Manage your clients, users, and access permissions</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleAddClient}>
            <span className="btn-icon">➕</span>
            Add Client
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="clients-stats">
        <div className="stat-item">
          <span className="stat-number">{clients.length}</span>
          <span className="stat-label">Total Clients</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">
            {clients.reduce((sum, c) => sum + c.stats.totalUsers, 0)}
          </span>
          <span className="stat-label">Total Users</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">
            {clients.reduce((sum, c) => sum + c.stats.activeUsers, 0)}
          </span>
          <span className="stat-label">Active Users</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">
            {formatFileSize(clients.reduce((sum, c) => sum + c.stats.storageUsed, 0))}
          </span>
          <span className="stat-label">Total Storage</span>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="clients-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>

        <div className="filter-controls">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="name">Sort by Name</option>
            <option value="users">Sort by Users</option>
            <option value="activity">Sort by Activity</option>
            <option value="files">Sort by Files</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Clients</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Clients List */}
      <div className="clients-list">
        {filteredAndSortedClients.map(client => (
          <div key={client.id} className="client-card">
            <div
              className="client-header"
              onClick={() => handleClientClick(client)}
            >
              <div className="client-info">
                <div className="client-name-row">
                  <h3 className="client-name">{client.name}</h3>
                  <span className={`status-badge ${client.status}`}>
                    {client.status}
                  </span>
                </div>
                <div className="client-stats-row">
                  <span className="stat">{client.stats.totalUsers} users</span>
                  <span className="stat">{client.stats.totalFiles} files</span>
                  <span className="stat">{formatFileSize(client.stats.storageUsed)}</span>
                  <span className="stat">Last active {formatTimeAgo(client.stats.lastActivity)}</span>
                </div>
              </div>

              <div className="client-actions">
                <button
                  className="expand-btn"
                  title={selectedClient?.id === client.id ? "Collapse" : "Expand"}
                >
                  {selectedClient?.id === client.id ? '▼' : '▶'}
                </button>
              </div>
            </div>

            {/* Expanded Client Details */}
            {selectedClient?.id === client.id && (
              <div className="client-details">
                <div className="details-header">
                  <h4>Team Members ({client.users.length})</h4>
                  <div className="header-buttons">
                    <button
                      className="btn-secondary"
                      onClick={() => handleManagePowerBI(client.id, client.name)}
                    >
                      <span className="btn-icon">📊</span>
                      Manage Power BI
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => handleInviteUser(client.id, client.name)}
                    >
                      <span className="btn-icon">👤</span>
                      Invite User
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => handleViewFiles(client.id, client.name)}
                      title="View and manage files for this client"
                    >
                      View Files
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => handleViewAsClient(client)}
                      title="View portal as this client sees it"
                    >
                      <span className="btn-icon">👁️</span>
                      View as Client
                    </button>
                  </div>
                </div>

                <div className="users-table">
                  <div className="table-header">
                    <div className="col-name">Name</div>
                    <div className="col-email">Email</div>
                    <div className="col-role">Role</div>
                    <div className="col-login">Last Login</div>
                    <div className="col-files">Files</div>
                    <div className="col-upload">Last Upload</div>
                    <div className="col-actions">Actions</div>
                  </div>

                  {client.users.map(user => (
                    <div key={user.email} className="table-row">
                      <div className="col-name">
                        <div className="user-avatar">
                          {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                        </div>
                        <span>{user.name || user.email.split('@')[0]}</span>
                      </div>
                      <div className="col-email">{user.email}</div>
                      <div className="col-role">
                        <span className={`role-badge ${user.role}`}>
                          {user.role}
                        </span>
                      </div>
                      <div className="col-login">{formatTimeAgo(user.lastLogin)}</div>
                      <div className="col-files">{user.filesUploaded}</div>
                      <div className="col-upload">{formatTimeAgo(user.lastUpload)}</div>
                      <div className="col-actions">
                        <button
                          className="action-btn"
                          title="Edit user"
                          onClick={() => handleEditUser(user)}
                        >
                          ✏️
                        </button>
                        <button
                          className="action-btn danger"
                          title="Remove user"
                          onClick={() => {
                            if (confirm(`Are you sure you want to remove ${user.name} from ${client.name}?`)) {
                              clientDB.removeUser(user.email);
                              loadClientsData();
                              alert(`${user.name} has been removed from ${client.name}`);
                            }
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}

                  {client.users.length === 0 && (
                    <div className="empty-state">
                      <p>No team members yet</p>
                      <button
                        className="btn-secondary"
                        onClick={() => handleInviteUser(client.id, client.name)}
                      >
                        Invite First User
                      </button>
                    </div>
                  )}
                </div>

                {/* Client Files Section */}
                <div className="client-section">
                  <div className="section-header">
                    <h5>Files for {client.name}</h5>
                    <button
                      className="btn-small btn-secondary"
                      onClick={() => handleViewFiles(client.id, client.name)}
                      title="View all files in File Management"
                    >
                      View All Files →
                    </button>
                  </div>
                  <div className="client-files">
                    {loadingFiles[client.id] ? (
                      <div className="loading-state-small">
                        <div className="loading-spinner-small"></div>
                        <p>Loading files...</p>
                      </div>
                    ) : clientFiles[client.id] && clientFiles[client.id].length > 0 ? (
                      <div className="files-table">
                        <div className="files-table-header">
                          <div className="col-file-name">File Name</div>
                          <div className="col-file-size">Size</div>
                          <div className="col-file-date">Last Modified</div>
                          <div className="col-file-actions">Actions</div>
                        </div>
                        {clientFiles[client.id].slice(0, 10).map(file => (
                          <div key={file.key} className="files-table-row">
                            <div className="col-file-name">
                              <span className="file-icon">
                                {isHtmlFile(file.name) ? '📄' : '📎'}
                              </span>
                              <span className="file-name-text">{file.name}</span>
                            </div>
                            <div className="col-file-size">{formatBytes(file.size)}</div>
                            <div className="col-file-date">
                              {new Date(file.lastModified).toLocaleDateString()}
                            </div>
                            <div className="col-file-actions">
                              <button
                                className="btn-file-action btn-download"
                                onClick={() => downloadFile(file.key, file.name)}
                                title="Download file"
                              >
                                Download
                              </button>
                              {isHtmlFile(file.name) && (
                                <button
                                  className={`btn-file-action btn-share ${copiedLink === file.key ? 'copied' : ''}`}
                                  onClick={() => generatePublicLink(file.key, file.name)}
                                  title="Generate shareable public link"
                                >
                                  {copiedLink === file.key ? '✓ Copied!' : 'Share Link'}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                        {clientFiles[client.id].length > 10 && (
                          <div className="view-more-files">
                            <button
                              className="btn-small btn-secondary"
                              onClick={() => handleViewFiles(client.id, client.name)}
                            >
                              View all {clientFiles[client.id].length} files →
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="empty-state-small">
                        <p>No files uploaded yet</p>
                        <button
                          className="btn-small btn-primary"
                          onClick={() => handleViewFiles(client.id, client.name)}
                        >
                          Upload Files
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Client Tasks Section */}
                <div className="client-section">
                  <div className="section-header">
                    <h5>Tasks for {client.name}</h5>
                    <button
                      className="btn-small btn-primary"
                      onClick={() => {
                        const taskTitle = prompt(`Create a new task for ${client.name}:`);
                        if (taskTitle && taskTitle.trim()) {
                          taskDB.createTask({
                            title: taskTitle,
                            description: '',
                            clientId: client.id,
                            tags: ['client-work'],
                            priority: 'medium'
                          });
                          loadClientsData(); // Refresh to show new task
                        }
                      }}
                    >
                      + Add Task
                    </button>
                  </div>
                  <div className="client-tasks">
                    {(() => {
                      const clientTasks = taskDB.getTasksByClient(client.id);
                      const activeTasks = clientTasks.filter(t => t.status !== 'completed');
                      const completedTasks = clientTasks.filter(t => t.status === 'completed');

                      return (
                        <>
                          {activeTasks.length === 0 && completedTasks.length === 0 ? (
                            <div className="empty-state-small">
                              <p>No tasks for this client yet</p>
                            </div>
                          ) : (
                            <>
                              {activeTasks.map(task => (
                                <div key={task.id} className="task-item">
                                  <div className="task-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={task.status === 'completed'}
                                      onChange={() => {
                                        taskDB.completeTask(task.id);
                                        loadClientsData();
                                      }}
                                    />
                                  </div>
                                  <div className="task-info">
                                    <div className="task-title-row">
                                      <span className="task-title">{task.title}</span>
                                      <div className="task-meta-small">
                                        {task.priority && (
                                          <span className={`priority-badge-small ${task.priority}`}>
                                            {task.priority}
                                          </span>
                                        )}
                                        {task.dueDate && (
                                          <span className="due-date-small">
                                            {new Date(task.dueDate).toLocaleDateString()}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    {task.description && (
                                      <p className="task-description-small">{task.description}</p>
                                    )}
                                    {task.tags.length > 0 && (
                                      <div className="task-tags-small">
                                        {task.tags.map(tag => (
                                          <span key={tag} className="tag-small">{tag}</span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    className="task-delete-btn"
                                    onClick={() => {
                                      if (confirm('Delete this task?')) {
                                        taskDB.deleteTask(task.id);
                                        loadClientsData();
                                      }
                                    }}
                                    title="Delete task"
                                  >
                                    🗑️
                                  </button>
                                </div>
                              ))}
                              {completedTasks.length > 0 && (
                                <details className="completed-tasks">
                                  <summary>Completed Tasks ({completedTasks.length})</summary>
                                  {completedTasks.map(task => (
                                    <div key={task.id} className="task-item completed">
                                      <div className="task-checkbox">
                                        <input
                                          type="checkbox"
                                          checked={true}
                                          onChange={() => {
                                            taskDB.reopenTask(task.id);
                                            loadClientsData();
                                          }}
                                        />
                                      </div>
                                      <div className="task-info">
                                        <span className="task-title">{task.title}</span>
                                      </div>
                                      <button
                                        className="task-delete-btn"
                                        onClick={() => {
                                          if (confirm('Delete this task?')) {
                                            taskDB.deleteTask(task.id);
                                            loadClientsData();
                                          }
                                        }}
                                        title="Delete task"
                                      >
                                        🗑️
                                      </button>
                                    </div>
                                  ))}
                                </details>
                              )}
                            </>
                          )}
                          <div className="view-all-tasks">
                            <a href="/tasks" onClick={(e) => {
                              e.preventDefault();
                              navigate('/tasks');
                            }}>
                              View all tasks in Task Manager →
                            </a>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Client Emails Section */}
                <div className="client-section">
                  <div className="section-header">
                    <h5>Emails & Communications</h5>
                    <button
                      className="btn-small btn-secondary"
                      onClick={() => {
                        alert('Email Integration Setup:\n\n1. Forward client emails to: tasks@portal.1209digital.com\n2. Or use the manual method: Copy email content and create a task\n3. The email will automatically create a task for this client\n\nSee EMAIL_TO_TASK_SETUP.md for full setup instructions.');
                      }}
                    >
                      ℹ️ Setup Guide
                    </button>
                  </div>
                  <div className="client-emails">
                    {(() => {
                      // Get tasks that were created from emails for this client
                      const clientTasks = taskDB.getTasksByClient(client.id);
                      const emailTasks = clientTasks.filter(t => t.emailReference);

                      return (
                        <>
                          {emailTasks.length === 0 ? (
                            <div className="empty-state-small">
                              <p>No email-based tasks yet</p>
                              <small>Forward emails to create tasks automatically</small>
                            </div>
                          ) : (
                            <div className="email-tasks-list">
                              {emailTasks.slice(0, 5).map(task => (
                                <div key={task.id} className="email-task-item">
                                  <div className="email-icon">📧</div>
                                  <div className="email-info">
                                    <div className="email-header-row">
                                      <div className="email-subject">{task.title}</div>
                                      <button
                                        className="expand-email-btn"
                                        onClick={() => toggleEmailBody(task.id)}
                                        title={expandedEmails[task.id] ? "Hide email body" : "Show email body"}
                                      >
                                        {expandedEmails[task.id] ? '▼' : '▶'}
                                      </button>
                                    </div>
                                    <div className="email-meta">
                                      <span className="email-meta-item">
                                        <strong>Date:</strong> {new Date(task.emailReference.receivedAt).toLocaleDateString()} {new Date(task.emailReference.receivedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                      </span>
                                      <span className="email-meta-item">
                                        <strong>To:</strong> {task.emailReference.to || 'portal@1209digital.com'}
                                      </span>
                                      <span className="email-meta-item">
                                        <strong>From:</strong> {task.emailReference.from}
                                      </span>
                                    </div>
                                    {expandedEmails[task.id] && task.emailReference.body && (
                                      <div className="email-body-expanded">
                                        <div className="email-body-label">Message:</div>
                                        <div className="email-body-content">
                                          {task.emailReference.body}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    className="btn-small"
                                    onClick={() => {
                                      navigate('/tasks');
                                    }}
                                  >
                                    View Task
                                  </button>
                                </div>
                              ))}
                              {emailTasks.length > 5 && (
                                <div className="view-more">
                                  <a href="/tasks" onClick={(e) => {
                                    e.preventDefault();
                                    navigate('/tasks');
                                  }}>
                                    View all {emailTasks.length} email tasks →
                                  </a>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Shared Resources Section */}
                <div className="client-section">
                  <div className="section-header">
                    <h5>Shared Resources</h5>
                    <button
                      className="btn-small btn-primary"
                      onClick={() => {
                        const resourceName = prompt('Resource Name (e.g., "Google Drive - Marketing Files"):');
                        if (!resourceName) return;

                        const resourceUrl = prompt('Enter the URL:');
                        if (!resourceUrl) return;

                        const resourceType = prompt('Type (google-drive, dropbox, onedrive, or other):', 'google-drive');

                        clientDB.addSharedResource(client.id, {
                          name: resourceName,
                          url: resourceUrl,
                          type: resourceType || 'other',
                          description: '',
                          addedBy: user.attributes?.email || user.username
                        });

                        loadClientsData();
                      }}
                    >
                      + Add Resource
                    </button>
                  </div>
                  <div className="shared-resources">
                    {(() => {
                      const resources = clientDB.getSharedResources(client.id);

                      const getResourceIcon = (type) => {
                        switch(type) {
                          case 'google-drive': return '🗂️';
                          case 'dropbox': return '📦';
                          case 'onedrive': return '☁️';
                          default: return '🔗';
                        }
                      };

                      return (
                        <>
                          {resources.length === 0 ? (
                            <div className="empty-state-small">
                              <p>No shared resources yet</p>
                              <small>Add links to Google Drive, Dropbox, or other shared folders</small>
                            </div>
                          ) : (
                            <div className="resources-list">
                              {resources.map(resource => (
                                <div key={resource.id} className="resource-item">
                                  <div className="resource-icon">
                                    {getResourceIcon(resource.type)}
                                  </div>
                                  <div className="resource-info">
                                    <div className="resource-name">{resource.name}</div>
                                    <div className="resource-type">{resource.type}</div>
                                    <div className="resource-date">
                                      Added {new Date(resource.addedDate).toLocaleDateString()}
                                    </div>
                                  </div>
                                  <div className="resource-actions">
                                    <a
                                      href={resource.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn-small"
                                      title="Open resource"
                                    >
                                      Open
                                    </a>
                                    <button
                                      className="btn-small btn-secondary"
                                      onClick={() => {
                                        const newName = prompt('Edit resource name:', resource.name);
                                        if (newName && newName !== resource.name) {
                                          clientDB.updateSharedResource(client.id, resource.id, {
                                            name: newName
                                          });
                                          loadClientsData();
                                        }
                                      }}
                                      title="Edit resource"
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      className="btn-small btn-danger"
                                      onClick={() => {
                                        if (confirm(`Delete "${resource.name}"?`)) {
                                          clientDB.removeSharedResource(client.id, resource.id);
                                          loadClientsData();
                                        }
                                      }}
                                      title="Delete resource"
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Keyword Data Section */}
                <div className="client-section">
                  <div className="section-header">
                    <h5>Keyword Research Data (AWS S3)</h5>
                  </div>
                  <KeywordDataBrowser company={client} />
                </div>

                <div className="client-footer">
                  <div className="client-settings">
                    <h5>Client Settings</h5>
                    <div className="settings-grid">
                      <div className="setting-item">
                        <label>Storage Limit</label>
                        <input type="text" value="50 GB" readOnly />
                      </div>
                      <div className="setting-item">
                        <label>User Limit</label>
                        <input type="text" value="25 users" readOnly />
                      </div>
                      <div className="setting-item">
                        <label>Created</label>
                        <input type="text" value="2024-01-15" readOnly />
                      </div>
                      <div className="setting-item">
                        <label>S3 Brand Folder</label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={client.s3BrandFolder || ''}
                            placeholder="e.g., oku-energy"
                            readOnly
                            style={{ flex: 1 }}
                          />
                          <button
                            className="btn-small btn-secondary"
                            onClick={() => {
                              const newFolder = prompt(
                                'Enter the S3 brand folder name for keyword data:\n\n' +
                                'This should match the folder name in AWS S3:\n' +
                                's3://prod-keyword-research-data-038462795459/brands/{folder-name}/',
                                client.s3BrandFolder || ''
                              );
                              if (newFolder !== null && newFolder !== client.s3BrandFolder) {
                                clientDB.updateCompanyS3BrandFolder(client.id, newFolder);
                                loadClientsData();
                                alert('S3 brand folder updated! Reload the client details to see keyword data.');
                              }
                            }}
                            title="Edit S3 brand folder mapping"
                          >
                            ✏️
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {filteredAndSortedClients.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🏢</div>
            <h3>No clients found</h3>
            <p>
              {searchTerm || filterStatus !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Add your first client to get started'
              }
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <button className="btn-primary" onClick={handleAddClient}>
                Add First Client
              </button>
            )}
          </div>
        )}
      </div>

      {/* Add Client Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={handleModalClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Client</h2>
              <button className="modal-close" onClick={handleModalClose}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-section">
                <h3>Company Information</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="clientName">Company Name *</label>
                    <input
                      id="clientName"
                      type="text"
                      value={newClient.name}
                      onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                      placeholder="Enter company name"
                      className="form-input"
                      required
                    />
                  </div>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="primaryColor">Primary Color</label>
                    <div className="color-input-group">
                      <input
                        id="primaryColor"
                        type="color"
                        value={newClient.primaryColor}
                        onChange={(e) => setNewClient({...newClient, primaryColor: e.target.value})}
                        className="color-input"
                      />
                      <input
                        type="text"
                        value={newClient.primaryColor}
                        onChange={(e) => setNewClient({...newClient, primaryColor: e.target.value})}
                        className="form-input"
                        placeholder="#1e40af"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="secondaryColor">Secondary Color</label>
                    <div className="color-input-group">
                      <input
                        id="secondaryColor"
                        type="color"
                        value={newClient.secondaryColor}
                        onChange={(e) => setNewClient({...newClient, secondaryColor: e.target.value})}
                        className="color-input"
                      />
                      <input
                        type="text"
                        value={newClient.secondaryColor}
                        onChange={(e) => setNewClient({...newClient, secondaryColor: e.target.value})}
                        className="form-input"
                        placeholder="#3b82f6"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="maxUsers">Max Team Members</label>
                    <input
                      id="maxUsers"
                      type="number"
                      value={newClient.maxTeamMembers}
                      onChange={(e) => setNewClient({...newClient, maxTeamMembers: parseInt(e.target.value)})}
                      min="1"
                      max="100"
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="storageLimit">Storage Limit (GB)</label>
                    <input
                      id="storageLimit"
                      type="number"
                      value={newClient.storageLimit}
                      onChange={(e) => setNewClient({...newClient, storageLimit: parseInt(e.target.value)})}
                      min="1"
                      max="1000"
                      className="form-input"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Admin User</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="adminName">Admin Name *</label>
                    <input
                      id="adminName"
                      type="text"
                      value={newClient.adminName}
                      onChange={(e) => setNewClient({...newClient, adminName: e.target.value})}
                      placeholder="Enter admin name"
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="adminEmail">Admin Email *</label>
                    <input
                      id="adminEmail"
                      type="email"
                      value={newClient.adminEmail}
                      onChange={(e) => setNewClient({...newClient, adminEmail: e.target.value})}
                      placeholder="admin@company.com"
                      className="form-input"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleModalClose}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleCreateClient}
                disabled={!newClient.name || !newClient.adminEmail || !newClient.adminName}
              >
                Create Client
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Power BI Manager Modal */}
      {showPowerBIModal && powerBIClientId && (
        <PowerBIManager
          clientId={powerBIClientId}
          clientName={powerBIClientName}
          onClose={handleClosePowerBI}
        />
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={handleCloseInvite}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Invite User to {inviteClientName}</h2>
              <button className="modal-close" onClick={handleCloseInvite}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-section">
                <h3>User Information</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="inviteEmail">Email Address *</label>
                    <input
                      id="inviteEmail"
                      type="email"
                      value={newInvite.email}
                      onChange={(e) => setNewInvite({...newInvite, email: e.target.value})}
                      placeholder="user@company.com"
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="inviteName">Full Name *</label>
                    <input
                      id="inviteName"
                      type="text"
                      value={newInvite.name}
                      onChange={(e) => setNewInvite({...newInvite, name: e.target.value})}
                      placeholder="John Doe"
                      className="form-input"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="inviteRole">Role</label>
                  <select
                    id="inviteRole"
                    value={newInvite.role}
                    onChange={(e) => setNewInvite({...newInvite, role: e.target.value})}
                    className="form-input"
                  >
                    <option value="viewer">Viewer - Can view files and reports</option>
                    <option value="editor">Editor - Can upload and edit content</option>
                    <option value="admin">Admin - Full access to client portal</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseInvite}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleSendInvitation}
                disabled={!newInvite.email || !newInvite.name}
              >
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && editingUser && (
        <div className="modal-overlay" onClick={handleCloseEditUser}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit User - {editingUser.name}</h2>
              <button className="modal-close" onClick={handleCloseEditUser}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-section">
                <h3>User Information</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="editUserName">Full Name *</label>
                    <input
                      id="editUserName"
                      type="text"
                      value={editUserData.name}
                      onChange={(e) => setEditUserData({...editUserData, name: e.target.value})}
                      placeholder="John Doe"
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="editUserEmail">Email Address *</label>
                    <input
                      id="editUserEmail"
                      type="email"
                      value={editUserData.email}
                      onChange={(e) => setEditUserData({...editUserData, email: e.target.value})}
                      placeholder="user@company.com"
                      className="form-input"
                      required
                      disabled
                      title="Email changes require re-invitation"
                    />
                    <small>To change email, remove user and send new invitation</small>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="editUserRole">Role</label>
                  <select
                    id="editUserRole"
                    value={editUserData.role}
                    onChange={(e) => setEditUserData({...editUserData, role: e.target.value})}
                    className="form-input"
                  >
                    <option value="viewer">Viewer - Can view files and reports</option>
                    <option value="editor">Editor - Can upload and edit content</option>
                    <option value="admin">Admin - Full access to client portal</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseEditUser}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleUpdateUser}
                disabled={!editUserData.name || !editUserData.email || !editUserData.role}
              >
                Update User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientManager;